import React from 'react';
import snakeCase from 'lodash/snakeCase';
import useOpportunityConfidenceLevels from '@/hooks/swr/useOpportunityConfidenceLevels';
import useOpportunityStages from '@/hooks/swr/useOpportunityStages';
import { useQueryProvider } from '@/modules/QueryProvider';
import GroupingType from '../constants/GroupingType';
import type OpportunitiesFilterParamsType from '../types/OpportunitiesFilterParams';

type OpportunityGroup = {
  id: string;
  prefix: string;
  name: string;
};

export default function useGroupingData(type: GroupingType) {
  const { data: { opportunityConfidenceLevels } = {} } =
    useOpportunityConfidenceLevels(type === GroupingType.CONFIDENCE_LEVEL);
  const { data: { opportunityStages } = {} } = useOpportunityStages(
    type === GroupingType.STAGE,
  );

  const { filters } = useQueryProvider<OpportunitiesFilterParamsType>();

  return React.useMemo((): { id: string; name: string; prefix: string }[] => {
    if (type === GroupingType.CONFIDENCE_LEVEL && opportunityConfidenceLevels) {
      return opportunityConfidenceLevels
        .sort(({ amount: a }, { amount: b }) => a - b)
        .reduce<OpportunityGroup[]>((prev, { confidenceLevelId, name }) => {
          /**
           * If we are grouping by confidence level and filters are applied, the
           * UI should limit the groups to the filtered confidence levels.
           */
          if (
            filters.confidenceLevelIds?.length &&
            !filters.confidenceLevelIds.includes(confidenceLevelId)
          ) {
            return prev;
          }

          return [
            ...prev,
            {
              id: confidenceLevelId,
              prefix: snakeCase(name),
              name,
            },
          ];
        }, []);
    }

    if (type === GroupingType.STAGE && opportunityStages) {
      return opportunityStages
        .sort(({ order: a }, { order: b }) => a - b)
        .reduce<OpportunityGroup[]>((prev, { opportunityStageId, name }) => {
          /**
           * If we are grouping by opportunity stage and filters are applied, the
           * UI should limit the groups to the filtered stages.
           */
          if (
            filters.opportunityStageIds?.length &&
            !filters.opportunityStageIds.includes(opportunityStageId)
          ) {
            return prev;
          }

          return [
            ...prev,
            {
              id: opportunityStageId,
              prefix: snakeCase(name),
              name,
            },
          ];
        }, []);
    }

    return [];
  }, [opportunityConfidenceLevels, opportunityStages, filters]);
}
